// useOverlayAnchor — position a floating overlay at a pointer, correctly, once.
//
// A dozen menus across the content browser and the three graph editors each
// hand-rolled the same four lines, and each carried the same two defects:
//
//   1. MIXED COORDINATE SPACES. The app root carries a CSS zoom for the
//      UI-scale setting (uiScale.js) whose default step is 1.1, so a raw
//      clientX written to a `position: fixed` left paints at clientX * zoom.
//      Measured on the asset context menu: right-clicking at (622, 634) wrote
//      `left: 622px` and painted at 684 — the drift grows with distance from
//      the origin, which is why it reads as the menu being "way off" rather
//      than as a scale factor. `window.innerWidth` is the wrong ruler for the
//      same reason, so the clamp that keeps a menu on-screen believed it had
//      10% more room than it had.
//
//   2. HAND-ESTIMATED HEIGHTS. `Math.min(y, innerHeight - 300)`,
//      `- 80`, `36 + n*28` — one guess per menu, each rotting the moment an
//      item was added. Under-estimate and the tail renders below the viewport;
//      in the bottom dock, where every right-click is near the bottom edge,
//      the unreachable tail is most of the menu.
//
// So: measure the element, convert through its own cumulative zoom
// (uiZoomCoords — which is a no-op inside `.react-flow`, where the root zoom is
// deliberately undone, so the same call is right in both places), and re-clamp
// on size change because menu items mount asynchronously.
//
// The returned props also stamp [data-aos-menu], which is what styles/index.css
// hangs the shared open animation and press feedback on — so a menu gets the
// feedback by being positioned correctly, with nothing per-item to remember.

import { useLayoutEffect, useRef, useState } from 'react'
import { pointerToOverlaySpace, overlayViewport } from './uiZoomCoords'

const MARGIN = 6

/**
 * @param {number} x viewport pointer X (event.clientX)
 * @param {number} y viewport pointer Y (event.clientY)
 * @param {object} [opts]
 * @param {number} [opts.margin] gap kept from the viewport edges
 * @param {number} [opts.submenuWidth] if given, `flip` reports whether a
 *   submenu of that width would leave the viewport when opened to the right.
 * @returns {{ref, props, flip, zoom}} spread `props` onto the overlay's root.
 */
export function useOverlayAnchor(x, y, { margin = MARGIN, submenuWidth = 0 } = {}) {
  const ref = useRef(null)
  const [pos, setPos] = useState(null)

  useLayoutEffect(() => {
    const el = ref.current
    if (!el || typeof window === 'undefined') return
    const apply = () => {
      const { vw, vh, zoom } = overlayViewport(el)
      const { x: px, y: py } = pointerToOverlaySpace(x, y, el)
      // getBoundingClientRect reports ZOOMED pixels; the clamp is written in
      // the element's own units, so the measured box is converted too.
      const r = el.getBoundingClientRect()
      const w = r.width / zoom
      const h = r.height / zoom
      const next = {
        left: Math.max(margin, Math.min(px, vw - w - margin)),
        top: Math.max(margin, Math.min(py, vh - h - margin)),
        // The clamp alone cannot save an overlay TALLER than the viewport: it
        // pins the top at `margin` and the tail still runs off the bottom,
        // unreachable. Cap the height at what the window can show and let it
        // scroll instead. Reported on the asset dock, where every right-click
        // is near the bottom edge. In the element's own units, like the rest
        // of this arithmetic.
        maxHeight: Math.max(0, vh - 2 * margin),
        // Decided from the CLAMPED left, so it accounts for the menu having
        // already been pulled inward from the edge.
        flip: submenuWidth > 0 && Math.min(px, vw - w - margin) + w + submenuWidth > vw,
        zoom,
      }
      // Functional set + equality guard: ResizeObserver → setPos → render →
      // observer callback again would otherwise ping-pong forever.
      setPos((p) => (p && p.left === next.left && p.top === next.top
        && p.flip === next.flip && p.zoom === next.zoom
        && p.maxHeight === next.maxHeight ? p : next))
    }
    apply()
    const ro = new ResizeObserver(apply)
    ro.observe(el)
    return () => ro.disconnect()
  }, [x, y, margin, submenuWidth])

  // Pre-measure paint: the same conversion without the size clamp, because
  // nothing has been laid out yet. Converting here too matters — an unzoomed
  // first paint followed by the corrected one is a visible jump, and the
  // element is on screen for that frame.
  const zoom = pos?.zoom ?? (ref.current ? overlayViewport(ref.current).zoom : 1)
  const left = pos?.left ?? (x / zoom)
  const top = pos?.top ?? (y / zoom)
  // Pre-measure: the viewport in the element's own units is already knowable,
  // so the cap applies on the first paint too rather than one frame late.
  const maxHeight = pos?.maxHeight
    ?? (ref.current ? Math.max(0, overlayViewport(ref.current).vh - 2 * margin) : undefined)

  return {
    ref,
    flip: pos?.flip ?? false,
    zoom,
    // Exposed separately so a caller with its own shorter cap can take the
    // MINIMUM rather than overriding this one — a menu that wants to be at
    // most 420px still must not exceed the window on a short screen.
    maxHeight,
    props: {
      ref,
      'data-aos-menu': '',
      style: { position: 'fixed', left, top, maxHeight, overflowY: 'auto' },
    },
  }
}
